import React, { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AtSign, Lock, Loader2, ArrowLeft, ShieldCheck, Heart, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { homeFor } from "@/lib/roles";
import LanguageToggle from "@/components/layout/LanguageToggle";

// Replace this URL to change the photo
const HERO_IMAGE =
    "https://images.pexels.com/photos/1648377/pexels-photo-1648377.jpeg?auto=compress&cs=tinysrgb&w=1400";

const STATS = [
    { value: "500+",  label: "Partner clinics" },
    { value: "50K+",  label: "Active members"  },
    { value: "30%",   label: "Avg. savings"     },
];

const FEATURES = [
    { icon: ShieldCheck, en: "Trusted network",   ar: "شبكة موثوقة"   },
    { icon: Heart,       en: "Real healthcare",   ar: "رعاية حقيقية"  },
    { icon: Sparkles,    en: "Exclusive savings", ar: "خصومات حصرية"  },
];

export default function LoginPage() {
    const { user, login } = useAuth();
    const { t, lang, isRTL } = useI18n();
    const location = useLocation();

    const [identifier, setIdentifier] = useState("");
    const [password,   setPassword]   = useState("");
    const [submitting, setSubmitting]  = useState(false);
    const [error,      setError]       = useState("");

    if (user && user.role) {
        const next = location.state?.from || homeFor(user.role);
        return <Navigate to={next} replace />;
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        const res = await login(identifier.trim().toLowerCase(), password);
        setSubmitting(false);
        if (!res.ok) {
            // Localised messages for the most common cases; fall back to the
            // smart description (network error, server detail, 5xx, etc.).
            if (res.status === 429) setError(t("auth.lockedOut"));
            else if (res.status === 401) setError(t("auth.wrongCreds"));
            else setError(res.error);
        }
    };

    return (
        <div className="min-h-screen w-full flex" dir={isRTL ? "rtl" : "ltr"} data-testid="login-page">

            {/* ── Left / Form panel ─────────────────────────────────── */}
            <div className="relative flex flex-col w-full md:w-1/2 shrink-0 bg-background">

                {/* Top bar */}
                <div className="flex items-center justify-between px-8 pt-7">
                    <a
                        href="/"
                        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        {lang === "ar" ? "العودة إلى الموقع" : "Back to site"}
                    </a>
                    <LanguageToggle />
                </div>

                {/* Form area */}
                <div className="flex-1 flex flex-col justify-center px-8 sm:px-14 lg:px-20 py-10">
                    <div className="w-full max-w-md mx-auto">

                        {/* Logo + heading */}
                        <div className="mb-8">
                            <img src="/hemaia_logo.svg" alt="Hemaia" className="h-10 mb-7" />
                            <h1 className="text-2xl font-semibold tracking-tight" data-testid="login-title">
                                {t("auth.signInTitle")}
                            </h1>
                            <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                                {t("auth.signInSubtitle")}
                            </p>
                        </div>

                        {/* Form */}
                        <form onSubmit={onSubmit} className="space-y-4" noValidate>
                            <div>
                                <label htmlFor="identifier" className="block text-sm font-medium mb-1.5">
                                    {t("auth.identifier")}
                                </label>
                                <div className="relative">
                                    <AtSign className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                    <input
                                        id="identifier"
                                        type="text"
                                        autoComplete="username"
                                        required
                                        autoFocus
                                        value={identifier}
                                        onChange={(e) => setIdentifier(e.target.value)}
                                        className="w-full ps-10 pe-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-shadow"
                                        data-testid="login-email-input"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="password" className="block text-sm font-medium mb-1.5">
                                    {t("auth.password")}
                                </label>
                                <div className="relative">
                                    <Lock className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                    <input
                                        id="password"
                                        type="password"
                                        autoComplete="current-password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full ps-10 pe-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-shadow"
                                        data-testid="login-password-input"
                                    />
                                </div>
                            </div>

                            {error && (
                                <div
                                    className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive"
                                    data-testid="login-error"
                                >
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={submitting || !identifier || !password}
                                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-95 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-1"
                                data-testid="login-submit-button"
                            >
                                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                {submitting ? t("auth.submitting") : t("auth.submit")}
                            </button>

                            <div className="text-center">
                                <a
                                    href="/forgot-password"
                                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    {lang === "ar" ? "نسيت كلمة المرور؟" : "Forgot your password?"}
                                </a>
                            </div>
                        </form>

                        {/* Cross-links */}
                        <div className="mt-6 pt-6 border-t border-border/50 text-center space-y-2">
                            <p className="text-sm text-muted-foreground">
                                {lang === "ar" ? "ليس لديك حساب؟" : "Don't have an account?"}{" "}
                                <a
                                    href="/signup"
                                    className="text-primary font-semibold hover:underline underline-offset-2"
                                >
                                    {lang === "ar" ? "إنشاء حساب جديد" : "Create one now"}
                                </a>
                            </p>
                            <p className="text-sm text-muted-foreground">
                                {lang === "ar" ? "من الموظفين أو المزودين؟" : "Staff or provider?"}{" "}
                                <a
                                    href="/staff/login"
                                    className="text-muted-foreground font-medium hover:text-foreground hover:underline underline-offset-2"
                                >
                                    {lang === "ar" ? "بوابة الموظفين →" : "Staff portal →"}
                                </a>
                            </p>
                        </div>

                        <p className="text-xs text-center text-muted-foreground mt-4">
                            {t("auth.needHelp")}
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <p className="text-center text-xs text-muted-foreground/40 pb-6 px-8">
                    © {new Date().getFullYear()} Hemaia Health Network
                </p>
            </div>

            {/* ── Right / Image panel ───────────────────────────────── */}
            <div className="hidden md:flex relative flex-1 overflow-hidden">

                {/* Photo */}
                <img
                    src={HERO_IMAGE}
                    alt="Happy family"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                    loading="eager"
                />

                {/* Gradient — heavy at bottom, light at top */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/5" />

                {/* Bottom content */}
                <div className="absolute bottom-0 start-0 end-0 p-10 text-white">
                    <p className="text-xs font-semibold uppercase tracking-widest text-white/50 mb-4">
                        {lang === "ar" ? "حماية — شبكة الولاء الصحي في مصر" : "Hemaia — Egypt's Healthcare Loyalty Network"}
                    </p>

                    <h2 className="text-3xl lg:text-4xl font-bold leading-tight">
                        {lang === "ar" ? "رعاية أفضل\nلكل عائلة مصرية" : "Better healthcare\nfor every family"}
                    </h2>

                    <p className="mt-3 text-sm text-white/65 max-w-sm leading-relaxed">
                        {lang === "ar"
                            ? "وفّر حتى ٣٠٪ على كل زيارة طبية مع شبكة عيادات شريكة في جميع أنحاء مصر."
                            : "Save up to 30% on every medical visit across our partner clinic network across Egypt."}
                    </p>

                    {/* Feature pills — placed here so eyes land on them naturally */}
                    <div className="flex flex-wrap gap-2 mt-5">
                        {FEATURES.map(({ icon: Icon, en, ar }) => (
                            <div
                                key={en}
                                className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-3.5 py-1.5 text-white text-xs font-medium"
                            >
                                <Icon className="h-3.5 w-3.5 text-white/80 shrink-0" />
                                {lang === "ar" ? ar : en}
                            </div>
                        ))}
                    </div>

                    {/* Stats */}
                    <div className="flex gap-8 mt-6 pt-6 border-t border-white/15">
                        {STATS.map(({ value, label }) => (
                            <div key={label}>
                                <p className="text-2xl font-bold">{value}</p>
                                <p className="text-xs text-white/55 mt-0.5">{label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

        </div>
    );
}
